// LagerPro auf dem Rechner — als Desktop-App (Electron-Hülle aus desktop/)
// oder als installierte Web-App (Chrome/Edge „Installieren", Safari „Zum Dock").
//
// Beide merken sich die Anmeldung wie die Handy-App: einmal Passwort (und
// Zweitfaktor), danach holt die App sich mit einem Gerätemerkmal selbst eine
// Sitzung. Der Server lässt Desktop-Geräte nach 90 Tagen ablaufen — dann
// fragt die App wieder nach dem Passwort.
//
// Wo das Merkmal liegt:
//   Desktop-App        im Schlüsselbund des Systems (Electron safeStorage —
//                      Keychain auf dem Mac, DPAPI unter Windows)
//   installierte Web-App  im Speicher der Seite (localStorage) — mehr gibt
//                      der Browser nicht her. Nur dort, nicht im normalen Tab.
import { istNativ } from '../../../gemeinsam/lib/plattform';

/** Was die Electron-Hülle über ihr Vorladeskript ins Fenster legt. */
export type DesktopBruecke = {
  plattform: 'desktop';
  system: 'mac' | 'windows' | 'linux';
  architektur: string;
  version: string;
  merkmalLesen: () => Promise<string | null>;
  merkmalSpeichern: (merkmal: string) => Promise<boolean>;
  merkmalLoeschen: () => Promise<void>;
};

export const bruecke = (): DesktopBruecke | undefined =>
  (globalThis as typeof globalThis & { lagerproDesktop?: DesktopBruecke }).lagerproDesktop;

/** Läuft die Seite in der Desktop-App? */
export const istDesktopApp = () => !!bruecke();

/** Als Web-App installiert und im eigenen Fenster geöffnet? */
export function istInstallierteWebApp(): boolean {
  if (istNativ() || istDesktopApp()) return false;
  try {
    return matchMedia('(display-mode: standalone)').matches
      || matchMedia('(display-mode: window-controls-overlay)').matches
      || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  } catch { return false; }
}

/**
 * Läuft LagerPro als App — Handy, Desktop oder installiert? Dann gibt es keine
 * Startseite: wer die App geöffnet hat, will sich anmelden, nicht überzeugt werden.
 */
export const istApp = () => istNativ() || istDesktopApp() || istInstallierteWebApp();

/** Soll sich diese Umgebung die Anmeldung merken (90 Tage)? */
export const merktAnmeldung = () => istDesktopApp() || istInstallierteWebApp();

// ---------------------------------------------------------------------------
// Gerätemerkmal
// ---------------------------------------------------------------------------
const SPEICHER = 'lp_geraet_desktop';

export async function merkmalLesen(): Promise<string | null> {
  const b = bruecke();
  if (b) { try { return await b.merkmalLesen(); } catch { return null; } }
  try { return localStorage.getItem(SPEICHER); } catch { return null; }
}

export async function merkmalSpeichern(merkmal: string): Promise<boolean> {
  const b = bruecke();
  if (b) { try { return await b.merkmalSpeichern(merkmal); } catch { return false; } }
  try { localStorage.setItem(SPEICHER, merkmal); return true; } catch { return false; }
}

export async function merkmalLoeschen(): Promise<void> {
  const b = bruecke();
  if (b) { try { await b.merkmalLoeschen(); } catch { /* war keines da */ } return; }
  try { localStorage.removeItem(SPEICHER); } catch { /* egal */ }
}

/** Ein Merkmal ohne Wartezeit vermuten — damit die Anmeldeseite nicht aufblitzt. */
export function merkmalVermutlich(): boolean {
  if (istDesktopApp()) return true;
  try { return !!localStorage.getItem(SPEICHER); } catch { return false; }
}

export function systemName(): 'mac' | 'windows' | 'linux' | 'andere' {
  const b = bruecke();
  if (b) return b.system;
  const ua = navigator.userAgent;
  if (/Mac OS X|Macintosh/.test(ua) && !/iPhone|iPad/.test(ua)) return 'mac';
  if (/Windows/.test(ua)) return 'windows';
  if (/Linux/.test(ua) && !/Android/.test(ua)) return 'linux';
  return 'andere';
}

/** Angaben für die Geräteliste in den Einstellungen. */
export function geraeteAngaben() {
  const b = bruecke();
  const system = { mac: 'Mac', windows: 'Windows', linux: 'Linux', andere: 'Rechner' }[systemName()];
  return b
    ? { name: `LagerPro für ${system}`, plattform: 'desktop', modell: `${system} ${b.architektur}`, version: b.version }
    : { name: `LagerPro Web-App (${system})`, plattform: 'pwa', modell: navigator.userAgent.slice(0, 80), version: '' };
}

// ---------------------------------------------------------------------------
// „Installieren"-Knopf des Browsers
// ---------------------------------------------------------------------------
// Chrome und Edge melden mit `beforeinstallprompt`, dass die Seite sich
// installieren lässt — oft schon beim Laden, bevor React steht. Deshalb wird
// das Ereignis gleich beim Start abgefangen und hier aufbewahrt.

type InstallEreignis = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };

let ereignis: InstallEreignis | null = null;
let installiert = false;
const hoerer = new Set<() => void>();
const melden = () => hoerer.forEach(f => f());

export function installationVorbereiten() {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', e => {
    e.preventDefault();
    ereignis = e as InstallEreignis;
    melden();
  });
  window.addEventListener('appinstalled', () => { ereignis = null; installiert = true; melden(); });
}

export function installStand() {
  return { moeglich: !!ereignis, installiert: installiert || istInstallierteWebApp() || istDesktopApp() };
}

export function installStandBeobachten(f: () => void) {
  hoerer.add(f);
  return () => { hoerer.delete(f); };
}

/** Öffnet den Installationsdialog des Browsers. */
export async function installieren(): Promise<'angenommen' | 'abgelehnt' | 'nicht-moeglich'> {
  if (!ereignis) return 'nicht-moeglich';
  const e = ereignis;
  await e.prompt();
  const { outcome } = await e.userChoice;
  ereignis = null;
  melden();
  return outcome === 'accepted' ? 'angenommen' : 'abgelehnt';
}
